/*
 * NavigationPrompt
 *
 * Prompt to handle user navigation.
 * E.g: when user is creating a transaction (in review page) and he/she wants to go to other page,
 * then this prompt will be up to block the navigation and take user's confirmation about leaving the page.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomNavigationPrompt, { NavigationPromptProps as OptionProps } from "react-router-navigation-prompt";

import Button from "src/components-v2/Button";
import { ModalBody, ModalDialog, ModalHeader, ModalOverlay, ModalTitle, ModalWrapper } from "./NavigationPrompt.styled";

import { cleanRoute } from "src/services/helpers/store";
import { PropsWithDataQaid } from "src/types/componentProps";

export type NavigationPromptProps = PropsWithDataQaid<{
  /**
   * Set when the prompt should be openned
   */
  when?: boolean;

  /**
   * Additional props for CustomNavigationPrompt
   * More info about this component props, see:
   * https://github.com/ZacharyRSmith/react-router-navigation-prompt#api-1
   */
  options?: OptionProps;

  /**
   * Optional side-effect run when the user confirms leaving the page. If it returns a promise, the
   * navigation waits for it to settle before proceeding — use it for cleanup that must finish before
   * the next page loads, e.g. resetting server-side drafts so the destination doesn't re-read stale
   * data. Defaults to a no-op.
   */
  onConfirmLeave?: (() => void | Promise<void>) | null;
}>;

const defaultProps = {
  when: false,
  options: {}
};

function NavigationPrompt(props: NavigationPromptProps) {
  const { when, options, onConfirmLeave, "data-qaid": dataQaid } = props;
  const { t } = useTranslation("translation");

  // `isConfirming` drives the spinner/disabled UI; the ref is the synchronous re-entrancy guard so a
  // same-tick double click can't run the confirm twice (state updates are async and would race).
  const [isConfirming, setIsConfirming] = useState(false);
  const isConfirmingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function handleConfirm(callback: Function) {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    setIsConfirming(true);
    try {
      // Await so cleanup completes BEFORE navigation proceeds — otherwise the destination page can
      // re-fetch before the cleanup request lands. `onConfirmLeave` handles its own errors, so this
      // never rejects and navigation is never blocked by a failure.
      if (onConfirmLeave) await onConfirmLeave();
      callback();
      cleanRoute(true);
    } finally {
      isConfirmingRef.current = false;
      // On confirm the page usually unmounts as it navigates away; guard against a post-unmount set.
      if (isMountedRef.current) setIsConfirming(false);
    }
  }

  return (
    <CustomNavigationPrompt when={when} {...options} disableNative>
      {({ onConfirm, onCancel }) => (
        <ModalWrapper>
          <ModalOverlay onClick={isConfirming ? undefined : onCancel} />
          <ModalDialog data-qaid={dataQaid}>
            <ModalHeader>
              <ModalTitle>{t("prompt.title")}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p className="u-mb-7">
                {t("prompt.body")}
                <b>{t("prompt.body-bold")}</b>
              </p>
              <div className="u-text-right">
                <Button
                  data-qaid={`${dataQaid}-confirm`}
                  className="u-mr-2"
                  variant="secondary"
                  onClick={() => handleConfirm(onConfirm)}
                  activated={isConfirming}
                  disabled={isConfirming}
                  outline
                >
                  {t("prompt.confirm")}
                </Button>
                <Button data-qaid={`${dataQaid}-cancel`} variant="secondary" onClick={onCancel} disabled={isConfirming}>
                  {t("prompt.cancel")}
                </Button>
              </div>
            </ModalBody>
          </ModalDialog>
        </ModalWrapper>
      )}
    </CustomNavigationPrompt>
  );
}

NavigationPrompt.defaultProps = defaultProps;

export default NavigationPrompt;
